import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Header, VehicleRegisterPopup } from '../../Components/Index';
import { useService } from '../../Context/ServiceContext';
import {
  createCustomer,
  getCustomers,
  getCustomerVehicles,
  isValidIranianMobile,
  normalizePhone,
  searchCustomerByPhone,
} from '../../services/api/customerApi';
import { extractErrorMessage } from '../../services/api/errorHandler';
import { createService } from '../../services/api/serviceApi';
import CustomerSearchPanel from './components/CustomerSearchPanel';
import CustomerServiceSelection from './components/CustomerServiceSelection';
import NewCustomerForm from './components/NewCustomerForm';
import ServiceDetailsForm from './components/ServiceDetailsForm';
import ServiceOneIcon from './components/ServiceOneIcon';

const EMPTY_CUSTOMER_FORM = { firstName: '', lastName: '', phone: '' };

const normalizePlate = value => String(value || '')
  .replace(/[۰-۹]/g, digit => '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))
  .replace(/[٠-٩]/g, digit => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
  .replace(/ایران/g, '')
  .replace(/[\s\-|]/g, '')
  .replace(/[يى]/g, 'ی')
  .replace(/ك/g, 'ک')
  .toLowerCase();

const vehiclePlateKey = vehicle => normalizePlate(
  `${vehicle.plate1 || ''}${vehicle.plate3 || ''}${vehicle.plate2 || ''}${vehicle.plateEnd || ''}`,
);

const plateMismatchCount = (plate, query) => {
  if (!query || query.length < 3) return Number.POSITIVE_INFINITY;
  if (plate.startsWith(query)) return 0;
  let mismatches = 0;
  for (let index = 0; index < query.length; index += 1) {
    if (plate[index] !== query[index]) mismatches += 1;
  }
  return mismatches;
};

export default function ServiceOne() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSelection = useRef(location.state || {});
  const capturedPlate = useRef(initialSelection.current.recognizedPlate || '');
  const autoHandledPlate = useRef('');
  const plateCatalog = useRef(null);
  const requestVersion = useRef(0);
  const {
    setSelectedVehicle: setSelectedVehicleCtx,
    setSelectedCustomer,
    serviceDate,
    setServiceDate,
    currentMileage,
    updateMileage,
    pendingServices,
  } = useService();

  const [searchMode, setSearchMode] = useState(() =>
    initialSelection.current.recognizedPlate || initialSelection.current.searchMode === 'plate' ? 'plate' : 'phone');
  const [query, setQuery] = useState(() => initialSelection.current.recognizedPlate || normalizePhone(initialSelection.current.customerPhone || ''));
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(true);
  const [searchError, setSearchError] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER_FORM);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [creationError, setCreationError] = useState('');
  const [needsProfile, setNeedsProfile] = useState(false);

  const [customer, setCustomer] = useState(null);
  const [customerIsNew, setCustomerIsNew] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isRegisterPopupOpen, setIsRegisterPopupOpen] = useState(false);
  const [submittingServices, setSubmittingServices] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const selectVehicle = useCallback(vehicle => {
    setSelectedVehicle(vehicle);
    if (vehicle) {
      const mileage = Number(vehicle.currentMileage) || 0;
      updateMileage(mileage);
    } else {
      updateMileage(0);
    }
  }, [updateMileage]);

  const toggleVehicle = useCallback(vehicle => {
    selectVehicle(String(selectedVehicle?.id) === String(vehicle.id) ? null : vehicle);
  }, [selectVehicle, selectedVehicle]);

  const loadPlateCatalog = useCallback(async () => {
    if (plateCatalog.current) return plateCatalog.current;
    const first = await getCustomers({ page: 1, sort: 'newest' });
    const customers = [...first.data];
    const pages = Math.ceil(first.total / first.pageSize);
    if (pages > 1) {
      const remaining = await Promise.all(Array.from({ length: pages - 1 }, (_, index) =>
        getCustomers({ page: index + 2, sort: 'newest' })));
      remaining.forEach(batch => customers.push(...batch.data));
    }
    const catalog = [];
    for (let index = 0; index < customers.length; index += 8) {
      const batch = await Promise.all(customers.slice(index, index + 8).map(async item => {
        try { return { customer: item, vehicles: await getCustomerVehicles(item.id) }; }
        catch { return { customer: item, vehicles: [] }; }
      }));
      catalog.push(...batch);
    }
    plateCatalog.current = catalog;
    return catalog;
  }, []);

  const chooseCustomer = useCallback(async (selectedCustomer, matchingVehicle = null) => {
    const version = ++requestVersion.current;
    setCustomer(selectedCustomer);
    setCustomerIsNew(false);
    setVehicles([]);
    selectVehicle(matchingVehicle);
    setShowNewCustomer(false);
    setVehiclesLoading(true);
    setVehiclesError('');
    try {
      const list = selectedCustomer.vehicles || await getCustomerVehicles(selectedCustomer.id);
      if (version !== requestVersion.current) return;
      setVehicles(list);
      const presetId = initialSelection.current.vehicleId;
      const presetVehicle = matchingVehicle || list.find(vehicle => String(vehicle.id) === String(presetId)) || null;
      selectVehicle(presetVehicle);
      initialSelection.current = {};
    } catch (error) {
      if (version === requestVersion.current) setVehiclesError(extractErrorMessage(error, 'خطا در دریافت خودروهای مشتری'));
    } finally {
      if (version === requestVersion.current) setVehiclesLoading(false);
    }
  }, [selectVehicle]);

  useEffect(() => {
    let cancelled = false;
    const version = ++requestVersion.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        if (searchMode === 'phone') {
          const phoneQuery = normalizePhone(query);
          if (phoneQuery && phoneQuery.length < 2) { setResults([]); return; }
          const response = await getCustomers({ search: phoneQuery, page: 1, sort: 'newest' });
          if (cancelled || version !== requestVersion.current) return;
          const sorted = [...response.data].sort((a, b) => Number(normalizePhone(b.phone) === phoneQuery) - Number(normalizePhone(a.phone) === phoneQuery));
          setResults(sorted.map(item => ({ customer: item })));
          const initialPhone = normalizePhone(initialSelection.current.customerPhone || '');
          const initialCustomer = initialPhone && sorted.find(item => normalizePhone(item.phone) === initialPhone);
          if (initialCustomer) chooseCustomer(initialCustomer);
        } else {
          const plateQuery = normalizePlate(query);
          if (!plateQuery) {
            setResults([]);
          } else {
            const catalog = await loadPlateCatalog();
            if (cancelled || version !== requestVersion.current) return;
            const matches = [];
            catalog.forEach(entry => entry.vehicles.forEach(vehicle => {
              const plate = vehiclePlateKey(vehicle);
              const distance = plateMismatchCount(plate, plateQuery);
              const exact = plate === plateQuery;
              const near = !exact && plateQuery.length >= 3 && distance <= 1;
              if (exact || near) matches.push({ customer: entry.customer, matchingVehicle: vehicle, approximate: near });
            }));
            matches.sort((a, b) => Number(a.approximate) - Number(b.approximate));
            setResults(matches);
            const capturedPlateKey = normalizePlate(capturedPlate.current);
            if (capturedPlateKey && capturedPlateKey === plateQuery && autoHandledPlate.current !== plateQuery) {
              autoHandledPlate.current = plateQuery;
              if (matches.length > 0) {
                chooseCustomer(matches[0].customer, matches[0].matchingVehicle);
              } else {
                setCustomerForm(EMPTY_CUSTOMER_FORM);
                setCreationError('');
                setNeedsProfile(false);
                setShowNewCustomer(true);
              }
            }
          }
        }
      } catch (error) {
        if (!cancelled && version === requestVersion.current) setSearchError(extractErrorMessage(error, 'خطا در جستجوی مشتری'));
      } finally {
        if (!cancelled && version === requestVersion.current) setSearching(false);
      }
    }, 320);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchMode, query, loadPlateCatalog, chooseCustomer]);

  useEffect(() => () => { requestVersion.current += 1; }, []);

  const changeMode = mode => {
    requestVersion.current += 1;
    setSearchMode(mode);
    setQuery('');
    setResults([]);
    setSearchError('');
  };

  const openNewCustomerForm = () => {
    const phone = searchMode === 'phone' ? normalizePhone(query) : '';
    setCustomerForm({ ...EMPTY_CUSTOMER_FORM, phone });
    setCreationError('');
    setNeedsProfile(false);
    setShowNewCustomer(true);
  };

  const submitNewCustomer = async event => {
    event.preventDefault();
    const phone = normalizePhone(customerForm.phone);
    if (!customerForm.firstName.trim() || !isValidIranianMobile(phone)) {
      setCreationError('نام و شماره موبایل معتبر را وارد کنید.');
      return;
    }
    setCreatingCustomer(true);
    setCreationError('');
    setNeedsProfile(false);
    try {
      const fullName = `${customerForm.firstName.trim()} ${customerForm.lastName.trim()}`.trim();
      const created = await createCustomer(phone, fullName);
      setCustomer(created);
      setCustomerIsNew(true);
      setVehicles([]);
      setSelectedVehicle(null);
      setShowNewCustomer(false);
      plateCatalog.current = null;
      setIsRegisterPopupOpen(true);
    } catch (error) {
      if (error.response?.status === 409) {
        try {
          const existing = await searchCustomerByPhone(phone);
          if (existing) { await chooseCustomer(existing); return; }
        } catch (_) { /* Show the original registration error. */ }
      }
      setNeedsProfile(error.response?.status === 403);
      setCreationError(extractErrorMessage(error, 'خطا در ثبت مشتری'));
    } finally {
      setCreatingCustomer(false);
    }
  };

  const changeCustomer = () => {
    requestVersion.current += 1;
    setCustomer(null);
    setCustomerIsNew(false);
    setVehicles([]);
    setSelectedVehicle(null);
    setVehiclesError('');
    setSelectedCustomer(null);
    setSelectedVehicleCtx(null);
    updateMileage(0);
  };

  const handleVehicleRegistered = vehicle => {
    setVehicles(current => [vehicle, ...current]);
    selectVehicle(vehicle);
    plateCatalog.current = null;
  };

  const canContinue = Boolean(customer && selectedVehicle && serviceDate);
  const handleContinue = async () => {
    if (!canContinue) return;
    if (!pendingServices?.length) {
      setSubmitError('ابتدا سرویس‌های انجام‌شده را انتخاب و تأیید کنید.');
      return;
    }
    const selectedCustomerData = {
      id: customer.id,
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      phone: normalizePhone(customer.phone),
      isNew: customerIsNew,
    };
    setSelectedCustomer(selectedCustomerData);
    const mileage = Number(currentMileage) || 0;
    setSelectedVehicleCtx(selectedVehicle.id, {
      vehicle: selectedVehicle,
      currentMileage: mileage,
      lastServiceMileage: mileage,
    });
    setSubmittingServices(true);
    setSubmitError('');
    try {
      const results = [];
      for (const service of pendingServices) {
        const response = await createService({
          vehicleId: selectedVehicle.id,
          categoryId: service.backendCategoryId,
          type: service.action === 'L' ? 'REPAIR' : 'PERIODIC_KM_TIME',
          mileage,
          serviceDate,
          cost: service.cost ? Number(service.cost) : null,
          description: service.description || '',
          details: {
            serviceTitle: service.title,
            action: service.action === 'L' ? 'تعویض' : 'بازدید',
            brand: service.brand || '',
            partNumber: service.partNumber || '',
            warrantyMonths: service.warrantyMonths ? Number(service.warrantyMonths) : null,
          },
        });
        if (!response?.success) throw new Error(response?.message || `ثبت «${service.title}» انجام نشد`);
        results.push(response.data);
      }
      navigate('/ServiceThree', { state: { serviceData: {
        ...results[0],
        vehicle: selectedVehicle,
        customer: selectedCustomerData,
        mileage,
        serviceDate,
        categories: pendingServices.map(service => ({
          categoryId: service.id,
          title: service.title,
          action: service.action,
          brand: service.brand || '',
          cost: service.cost || null,
          description: service.description || '',
        })),
      } } });
    } catch (error) {
      setSubmitError(extractErrorMessage(error, 'ثبت سرویس‌ها با خطا مواجه شد'));
    } finally {
      setSubmittingServices(false);
    }
  };

  const exactPhone = searchMode === 'phone' && isValidIranianMobile(query) ? normalizePhone(query) : '';
  const hasExactCustomer = Boolean(exactPhone && results.some(result => normalizePhone(result.customer.phone) === exactPhone));
  const footerTitle = !customer
    ? 'ابتدا مشتری را انتخاب کنید'
    : (!selectedVehicle ? 'یک خودرو را انتخاب کنید' : (!serviceDate ? 'اطلاعات سرویس را کامل کنید' : 'همه‌چیز آماده ثبت است'));
  const footerHint = !customer
    ? 'جستجو با موبایل یا پلاک'
    : (!selectedVehicle ? 'یا خودروی جدید ثبت کنید' : `${(pendingServices?.length || 0).toLocaleString('fa-IR')} سرویس انتخاب شده`);

  return <>
    <Header hideR="" LinkR={customer ? "" : "/ServiceTwo"} onStartClick={customer ? changeCustomer : undefined} Text="انتخاب مشتری و خودرو" />
    <main className="service-one" dir="rtl">
      {!customer && !showNewCustomer && <CustomerSearchPanel
        mode={searchMode} onModeChange={changeMode} query={query} onQueryChange={setQuery}
        results={results} loading={searching} error={searchError} onSelect={chooseCustomer}
        onShowNewCustomer={openNewCustomerForm} exactPhone={exactPhone} hasExactCustomer={hasExactCustomer}
        recognizedPlate={capturedPlate.current}
      />}

      {!customer && showNewCustomer && <NewCustomerForm
        form={customerForm}
        onChange={(field, value) => setCustomerForm(current => ({ ...current, [field]: field === 'phone' ? normalizePhone(value) : value }))}
        onCancel={() => setShowNewCustomer(false)} onSubmit={submitNewCustomer}
        loading={creatingCustomer} error={creationError} needsProfile={needsProfile}
        recognizedPlate={capturedPlate.current}
        profileLink={<Link to="/ServiceWorkerRegister">تکمیل پروفایل سرویس‌کار</Link>}
      />}

      {customer && <CustomerServiceSelection
        customer={customer} vehicles={vehicles} selectedVehicle={selectedVehicle}
        onSelectVehicle={toggleVehicle}
        onAddVehicle={() => setIsRegisterPopupOpen(true)} loading={vehiclesLoading}
      />}
      {vehiclesError && <p role="alert" className="service-one__error">{vehiclesError}</p>}

      {customer && <ServiceDetailsForm
        currentMileage={currentMileage}
        serviceDate={serviceDate}
        onCurrentMileage={updateMileage}
        onDate={setServiceDate}
      />}

      {submitError && <p role="alert" className="service-one__error">{submitError}</p>}
      <footer className={`service-one__footer${canContinue ? ' is-ready' : ''}`}>
        <div className="service-one__footer-status"><b>{footerTitle}</b><small>{footerHint}</small></div>
        <button type="button" disabled={!canContinue || submittingServices} onClick={handleContinue}>{submittingServices ? 'در حال ثبت…' : 'ثبت و نمایش رسید'}{!submittingServices && <ServiceOneIcon name="chevron" />}</button>
      </footer>
    </main>

    <VehicleRegisterPopup
      isOpen={isRegisterPopupOpen}
      onClose={() => setIsRegisterPopupOpen(false)}
      customerId={customer?.id ?? null}
      customerPhone={normalizePhone(customer?.phone || customerForm.phone)}
      initialPlate={capturedPlate.current}
      onSuccess={handleVehicleRegistered}
    />
  </>;
}
